import { Id } from '@/infra/id'
import { safeParseDate } from '@/utils/date-util'
import type {
  ParticipantResource,
  ConversationResource,
} from './api/resources/messages'
import type {
  ConversationProps,
  ConversationMeta,
  JobMeta,
  UnknownMeta,
} from './Conversation'
import type { ParticipantProps } from './Participant'
import type { PrimeWorkspaceFn } from '@/features/iam'

/**
 * Maps a conversation resource to conversation model props.
 */
export function mapConversationResourceToProps(
  resource: ConversationResource,
  primeWorkspace: PrimeWorkspaceFn,
): ConversationProps {
  const meta = mapConversationMetaToMeta(resource.meta)

  return {
    id: Id(resource.id),
    workspace: primeWorkspace({ id: Id(resource.workspace_id) }),
    externalId: resource.external_id ?? null,
    meta,
    dateCreated: safeParseDate(resource.date_created),
    dateModified: safeParseDate(resource.date_modified),
    participants: resource.participants.map(mapParticipantResourceToProps),
  }
}

/**
 * Maps a participant resource to participant model props.
 */
export function mapParticipantResourceToProps(
  resource: ParticipantResource,
): ParticipantProps {
  return {
    userId: Id(resource.user_public_id),
    lastSeen: safeParseDate(resource.last_seen),
    muted: resource.muted,
    dateCreated: safeParseDate(resource.date_created),
    dateModified: safeParseDate(resource.date_modified),
  }
}

/**
 * Maps a conversation meta from resource to the appropriate ConversationMeta type.
 */
export function mapConversationMetaToMeta(
  meta: Record<string, unknown> | null | undefined,
): ConversationMeta {
  if (!meta) {
    return { type: 'Unknown' }
  }

  if ('type' in meta && meta.type === 'Job') {
    if ('id' in meta && typeof meta.id === 'string') {
      return {
        type: 'Job',
        id: Id(meta.id),
        name: 'name' in meta && typeof meta.name === 'string' ? meta.name : '',
      } satisfies JobMeta
    }
  }

  return { type: 'Unknown' } satisfies UnknownMeta
}
